import { PLAN_TYPES } from 'src/constants/cardsEnum';
import { isPlanAtLeast } from 'src/utils/plans';
import type {
  BillingTrialApiResponse,
  OrganizationApiResponseWithPopulatedPlan,
} from 'src/types/organization';

export type OrganizationBillingState =
  | 'free'
  | 'trial_active'
  | 'trial_expired'
  | 'paid';

const isBillingTrialCurrentlyActive = (
  billingTrial?: BillingTrialApiResponse | null,
  now: Date = new Date(),
) => {
  if (!billingTrial || billingTrial.status !== 'active') {
    return false;
  }

  return new Date(billingTrial.endsAt).getTime() > now.getTime();
};

const hasBillingTrialReachedEnd = (
  billingTrial?: BillingTrialApiResponse | null,
  now: Date = new Date(),
) => {
  if (!billingTrial || billingTrial.status !== 'active') {
    return false;
  }

  return new Date(billingTrial.endsAt).getTime() <= now.getTime();
};

export const getOrganizationCommercialPlanDisplayName = (
  organization?: Partial<OrganizationApiResponseWithPopulatedPlan> | null,
): keyof typeof PLAN_TYPES =>
  organization?.planId?.displayName ?? PLAN_TYPES.FREE;

export const hasActiveOrganizationBillingTrial = (
  organization?: Partial<OrganizationApiResponseWithPopulatedPlan> | null,
  now: Date = new Date(),
): boolean => isBillingTrialCurrentlyActive(organization?.billingTrial, now);

export const getOrganizationBillingTrialDaysRemaining = (
  organization?: Partial<OrganizationApiResponseWithPopulatedPlan> | null,
  now: Date = new Date(),
): number => {
  const billingTrial = organization?.billingTrial;

  if (!isBillingTrialCurrentlyActive(billingTrial, now)) {
    return 0;
  }

  return Math.max(
    1,
    Math.ceil(
      (new Date(billingTrial!.endsAt).getTime() - now.getTime()) /
        (24 * 60 * 60 * 1000),
    ),
  );
};

export const getOrganizationEffectivePlanDisplayName = (
  organization?: Partial<OrganizationApiResponseWithPopulatedPlan> | null,
  now: Date = new Date(),
): keyof typeof PLAN_TYPES => {
  if (hasActiveOrganizationBillingTrial(organization, now)) {
    return PLAN_TYPES.PRO;
  }

  return getOrganizationCommercialPlanDisplayName(organization);
};

export const getOrganizationBillingState = (
  organization?: Partial<OrganizationApiResponseWithPopulatedPlan> | null,
  now: Date = new Date(),
): OrganizationBillingState => {
  const commercialPlan = getOrganizationCommercialPlanDisplayName(organization);
  const billingTrial = organization?.billingTrial;

  if (hasActiveOrganizationBillingTrial(organization, now)) {
    return 'trial_active';
  }

  if (
    billingTrial?.status === 'expired' ||
    hasBillingTrialReachedEnd(billingTrial, now)
  ) {
    return commercialPlan === PLAN_TYPES.FREE ? 'trial_expired' : 'paid';
  }

  if (commercialPlan === PLAN_TYPES.FREE) {
    return 'free';
  }

  return 'paid';
};

export const isOrganizationPlanAtLeast = (
  organization:
    | Partial<OrganizationApiResponseWithPopulatedPlan>
    | null
    | undefined,
  benchmarkPlan: keyof typeof PLAN_TYPES,
  now: Date = new Date(),
): boolean =>
  isPlanAtLeast(
    getOrganizationEffectivePlanDisplayName(organization, now),
    benchmarkPlan,
  );
